// DTOs mirrored from Scout.Deals/Weekly.cs and Scout.Api WeeklyEndpoints — the weekly update as evidence-backed movement.
import type { Scope } from './orgs'

export interface WeekWindow {
  /** the Monday, YYYY-MM-DD */
  start: string
  /** the Sunday, YYYY-MM-DD */
  end: string
}

export type EvidenceKind = 'thread' | 'signal' | 'meeting' | 'contact' | 'tactic' | 'account'
/** The callout a movement card carries when a rival was in the room, or is being weighed against us. */
export const COMPETITOR_THREAT = 'competitor threat'
export type MovementTone = 'up' | 'down' | 'flat' | 'warn' | 'ok'
export type MovementSlice = 'pipeline' | 'accounts' | 'people' | 'tactics' | 'meetings'
export type CitationKind = 'pursuit' | 'account' | 'person' | 'meeting' | 'tactic'

export interface EvidenceLine {
  kind: EvidenceKind
  id: string
  label: string
  on: string | null
  detail: string | null
}

/** One thing that moved. `key` (E1, A2, P3, T4, M5) is what the narrative cites. */
export interface MovementItem {
  key: string
  slice: MovementSlice
  id: string
  title: string
  subtitle: string | null
  callout: string
  tone: MovementTone
  tags: string[]
  companyId: string | null
  companyName: string | null
  stageFrom: string | null
  stageTo: string | null
  amount: number | null
  on: string | null
  evidence: EvidenceLine[]
}

export interface SliceSummary {
  slice: MovementSlice
  title: string
  count: number
  ups: number
  downs: number
  /** how many of `count` carry a card; the strongest first */
  shown: number
}

export interface MeasuredFrom {
  threadsActive: number
  meetingsHeld: number
  signalsRaised: number
  contactsTouched: number
  hasBaseline: boolean
  baselineTakenOn: string | null
  fromStoredWeek: boolean
  /** the strategy pillar this measurement files under (DEC-2026-09-23-01) */
  pillar?: string
}

export interface WeeklyGlance {
  newPursuits: number
  stageUps: number
  stageDowns: number
  amountNamed: number
  amountsNamed: number
  meetingsHeld: number
  dormantResurfaced: number
  tacticsApplied: number
  tacticResults: number
  nextStepsOwed: number
  pipelineNow: number
  pipelineBefore: number | null
  openPursuits: number
  /** Rivals seen at our accounts this week. Read before anything else on the update. */
  threats: number
}

export interface AheadMeeting {
  key: string
  meetingId: string
  subject: string
  start: string
  end: string
  companyId: string | null
  companyName: string | null
  contacts: string[]
}

export interface AheadStep {
  key: string
  engagementId: string
  subject: string
  companyName: string
  companyId: string | null
  stage: string
  nextStep: string
  by: string | null
  mine: boolean
  overdue: boolean
}

export interface AheadTactic {
  tacticId: string
  tacticName: string
  category: string
  engagementId: string
  subject: string
  companyName: string
  why: string
  planned: boolean
  /** what a `play` correction names: the application's id when planned, else the suggestion's derived id */
  correctionId: string | null
}

export interface AheadContact {
  personId: string
  name: string
  title: string | null
  companyName: string
  companyId: string | null
  strength: number
  dormancyDays: number
  lastSubject: string | null
}

export interface WeekAhead {
  window: WeekWindow
  meetings: AheadMeeting[]
  steps: AheadStep[]
  tactics: AheadTactic[]
  contacts: AheadContact[]
}

export interface Citation {
  key: string
  kind: CitationKind
  id: string
  label: string
}

export interface WeeklyMovement {
  week: WeekWindow
  measuredFrom: MeasuredFrom
  glance: WeeklyGlance
  slices: SliceSummary[]
  items: MovementItem[]
  ahead: WeekAhead
}

export interface WeeklyNarrativeView {
  /** sections introduced by '# ' lines; facts cited as [E1, M2] */
  text: string
  modelWritten: boolean
  modelId: string | null
  writtenAt: string
  citations: Citation[]
}

export interface WeeklyView {
  movement: WeeklyMovement
  narrative: WeeklyNarrativeView | null
  /** every key the narrative may cite this week, with what it points at */
  citations: Citation[]
  modelConfigured: boolean
  /** the weekly-write job running now, if any */
  activeJobId: string | null
  isCurrentWeek: boolean
  allOwners: boolean
  /** which org this update covers (?org=mine|all|<key>); the kicker reads its name */
  scope: Scope
  calendarConnected: boolean
}

export interface WeeklyUpdateSummary {
  weeklyUpdateId: string
  weekStart: string
  weekEnd: string
  modelWritten: boolean
  modelId: string | null
  writtenAt: string
  headline: string
  words: number
  cited: number
  glance: WeeklyGlance | null
}
